// src/pages/Profile.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getDatabase, ref, get, onValue } from 'firebase/database';
import logo from '../assets/logo.png'
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  ArrowLeft, User, Mail, Phone, LogOut, 
  WifiOff, Trophy, Coins, Award, Crown, 
  ChevronRight, Users, FileText, Scale, Heart, Info, 
  Sword, Target, Medal, Gem, Zap, Calendar, 
  CheckCircle, Lock, Loader2, IndianRupee, Gamepad2,
  Edit2, Save, X, AlertCircle, Sparkles, TrendingUp,
  Shield, Star, Diamond, Gift, Clock, History, Ban
} from 'lucide-react';

// Safe cache interface - only non-sensitive data
interface SafeCache {
  name: string;
  userName: string;
  defaultTier: string;
  matchPlayed: number;
  finishes: number;
  AccountStatus: boolean;
}

interface UserProfile {
  name: string;
  userName: string;
  email: string;
  phone: string;
  balance: number;
  winnings: number;
  matchPlayed: number;
  finishes: number;
  defaultTier: string;
  AccountStatus: boolean;
  createdAt?: number;
  myTeamId?: string;
  BGMIID?: string;
  BGMIIGN?: string;
  FFMAXID?: string;
  FFMAXIGN?: string;
}

interface TierInfo {
  name: string;
  displayName: string;
  color: string;
  bgColor: string;
  borderColor: string;
  gradient: string;
  icon: React.ReactNode;
  status: 'current' | 'locked' | 'unlocked';
  requirements: string;
}

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  
  // Edit Game Credentials State
  const [editingGame, setEditingGame] = useState<'BGMI' | 'FFMAX' | null>(null);
  const [editIGN, setEditIGN] = useState('');
  const [editID, setEditID] = useState('');
  const [saving, setSaving] = useState(false);

  // Refs for cleanup
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);

const allTiers: TierInfo[] = [
  {
    name: 'S',
    displayName: 'LEGEND (S)',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    gradient: 'from-yellow-400 to-orange-500',
    icon: <Crown className="w-8 h-8" />,
    status: 'locked',
    requirements: 'You are in top 1%'
  },
  {
    name: 'T1',
    displayName: 'ELITE (T1)',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    gradient: 'from-purple-400 to-pink-500',
    icon: <Gem className="w-8 h-8" />,
    status: 'locked',
    requirements: 'You are in top 5%'
  },
  {
    name: 'T2',
    displayName: 'PRO (T2)',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    gradient: 'from-blue-400 to-cyan-500',
    icon: <Medal className="w-8 h-8" />,
    status: 'locked',
    requirements: 'Your are in top 20%'
  },
  // 🆕 ADD T2Q HERE - Between T2 and T3
  {
    name: 'T2Q',
    displayName: 'PRO (T2Q)',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    gradient: 'from-cyan-400 to-teal-500',
    icon: <Star className="w-8 h-8" />, // Using Star icon (you can change this)
    status: 'locked',
    requirements: 'Your are in top 50%'
  },
  {
    name: 'T3',
    displayName: 'RISING (T3)',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    gradient: 'from-green-400 to-emerald-500',
    icon: <Zap className="w-8 h-8" />,
    status: 'locked',
    requirements: 'Just Start Playing'
  }
];

  // ✅ SECURITY FIX: Use Cloud Function instead of direct write
  const updateGameCredentialsViaCloudFunction = async (game: 'BGMI' | 'FFMAX', ign: string, gameId: string) => {
    try {
      const functions = getFunctions();
      const updateCredentials = httpsCallable(functions, 'updateGameCredentials');
      
      const result = await updateCredentials({
        game,
        ign,
        gameId
      });
      
      return result.data;
    } catch (error: any) {
      console.error('Cloud Function error:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to update credentials');
      } else if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update these credentials');
      } else if (error.code === 'invalid-argument') {
        throw new Error(error.message || 'Invalid input provided');
      } else if (error.code === 'already-exists') {
        throw new Error('These credentials are already in use');
      } else {
        throw new Error(error.message || 'Failed to update credentials. Please try again.');
      }
    }
  };

  // SECURITY FIX: Proper useEffect with cleanup
  useEffect(() => {
    isMountedRef.current = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!isMountedRef.current) return;

      if (!currentUser) {
        navigate('/login');
        setLoading(false);
        return;
      }
      
      setUser(currentUser);
      
      // Check for banned status immediately
      const db = getDatabase();
      const userRef = ref(db, `Accounts/${currentUser.uid}`);
      
      try {
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.AccountStatus === false) {
            setIsBanned(true);
            setLoading(false);
            // Sign out banned user
            await signOut(auth);
            navigate('/account-suspended');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking account status:', error);
      }
      
      // Set up realtime listener with proper cleanup
      setupProfileListener(currentUser.uid);
    });
    
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      unsubscribe();
    };
  }, [navigate]);

  // SECURITY FIX: Single listener with proper cleanup
  const setupProfileListener = (userId: string) => {
    // Clean up existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const db = getDatabase();
    const userRef = ref(db, `Accounts/${userId}`);
    
    unsubscribeRef.current = onValue(userRef, async (snapshot) => {
      if (!isMountedRef.current) return;

      if (snapshot.exists()) {
        const data = snapshot.val();
        
        // Check for ban
        if (data.AccountStatus === false) {
          setIsBanned(true);
          await signOut(auth);
          navigate('/account-suspended');
          return;
        }
        
        const profileData: UserProfile = {
          name: data.name || '',
          userName: data.userName || '',
          email: user?.email || '',
          phone: data.phone || '',
          balance: data.balance || 0,
          winnings: data.winnings || 0,
          matchPlayed: data.matchPlayed || 0,
          finishes: data.finishes || 0,
          defaultTier: data.defaultTier || 'T3',
          AccountStatus: data.AccountStatus !== false,
          createdAt: data.createdAt || Date.now(),
          myTeamId: data.myTeamId,
          BGMIID: data.BGMIID,
          BGMIIGN: data.BGMIIGN,
          FFMAXID: data.FFMAXID,
          FFMAXIGN: data.FFMAXIGN,
        };
        
        setProfile(profileData);
        
        // SECURITY FIX: Only cache safe data
        const safeCache: SafeCache = {
          name: profileData.name,
          userName: profileData.userName,
          defaultTier: profileData.defaultTier,
          matchPlayed: profileData.matchPlayed,
          finishes: profileData.finishes,
          AccountStatus: profileData.AccountStatus
        };
        localStorage.setItem(`profile_safe_${userId}`, JSON.stringify(safeCache));
      
        setLoading(false);
      } else {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error loading profile:', error);
      // Try to load safe cache
      const cached = localStorage.getItem(`profile_safe_${userId}`);
      if (cached) {
        const safeData: SafeCache = JSON.parse(cached);
        // Create minimal profile from safe cache
        setProfile({
          name: safeData.name,
          userName: safeData.userName,
          email: user?.email || '',
          phone: '',
          balance: 0,
          winnings: 0,
          matchPlayed: safeData.matchPlayed || 0,
          finishes: safeData.finishes || 0,
          defaultTier: safeData.defaultTier || 'T3',
          AccountStatus: safeData.AccountStatus !== false,
          createdAt: Date.now(),
        });
      }
      setLoading(false);
    });
  };

// SECURITY FIX: Clean localStorage on logout
const handleLogout = async () => {
  setLoggingOut(true);
  
  try {
    if (user?.uid) {
      localStorage.removeItem(`profile_safe_${user.uid}`);
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    await signOut(auth);
    navigate('/');
  } catch (error) {
    console.error('Error logging out:', error);
    alert('Failed to logout. Please try again.');
  } finally {
    setLoggingOut(false);
    setShowLogoutModal(false);
  }
};

  // ✅ Updated to use Cloud Function
  const handleUpdateGameCredentials = async (game: 'BGMI' | 'FFMAX') => {
    if (!editIGN || !editID) {
      alert('Please fill in both IGN and ID fields');
      return;
    }
    
    setSaving(true);
    try {
      // Call Cloud Function instead of direct Firebase write
      await updateGameCredentialsViaCloudFunction(game, editIGN, editID);
      
      // Update local state
      setProfile(prev => prev ? {
        ...prev,
        [game === 'BGMI' ? 'BGMIIGN' : 'FFMAXIGN']: editIGN,
        [game === 'BGMI' ? 'BGMIID' : 'FFMAXID']: editID,
      } : null);
      
      alert(`${game} credentials updated successfully!`);
      setEditingGame(null);
      setEditIGN('');
      setEditID('');
      
    } catch (error: any) {
      console.error('Error updating game credentials:', error);
      alert(error.message || 'Failed to update credentials. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getTiersWithStatus = (): TierInfo[] => {
    const userCurrentTier = profile?.defaultTier || 'T3';
    const userRank = getTierLevel(userCurrentTier);
    
    return allTiers.map(tier => {
      const tierRank = getTierLevel(tier.name);
      
      if (tier.name === userCurrentTier) {
        return { ...tier, status: 'current' };
      } 
      else if (tierRank < userRank) {
        return { ...tier, status: 'unlocked' };
      }
      else {
        return { ...tier, status: 'locked' };
      }
    });
  };

const getTierLevel = (tierName: string): number => {
  switch(tierName) {
    case 'S': return 5;      // Highest
    case 'T1': return 4;
    case 'T2': return 3;
    case 'T2Q': return 2;    // 🆕 Between T2 and T3
    case 'T3': return 1;
    default: return 0;
  }
};

  const tiersWithStatus = getTiersWithStatus();
  const currentTier = tiersWithStatus.find(t => t.name === profile?.defaultTier) || tiersWithStatus[3];

  const formatJoinDate = (timestamp?: number) => {
    if (!timestamp) return 'Recently';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle banned user
  if (isBanned) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] to-[#1a1a2e] flex items-center justify-center px-4">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Ban className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-white font-rajdhani font-bold text-2xl mb-2">Account Suspended</h2>
          <p className="text-gray-400 mb-6">
            Your account has been suspended. Please contact support for more information.
          </p>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl text-white font-rajdhani font-bold"
          >
            Return Home
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center overflow-hidden">
    <div className="absolute w-60 h-60 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
    <div className="absolute w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ top: '20%', left: '10%' }} />
    <div className="absolute w-56 h-56 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ bottom: '20%', right: '10%' }} />
    
    <div className="relative flex flex-col items-center">
      <div className="relative w-28 h-28">
        <div className="absolute inset-0 rounded-full">
          <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A855F7" />
                <stop offset="50%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke="url(#ringGradient)"
              strokeWidth="3"
              strokeDasharray="200"
              strokeDashoffset="50"
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-pink-500/20 blur-xl rounded-full animate-pulse" />
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-pink-500/10 rounded-full blur-md animate-pulse-slow" />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={logo}
            alt="ArenaX"
            className="w-20 h-20 object-contain animate-float"
          />
        </div>
      </div>

      <div className="mt-5 text-center">
        <p className="text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text font-rajdhani font-bold text-base tracking-widest animate-pulse">
          LOADING ARENA...
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>
    </div>
  </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f0f1a] to-[#0a0a0f]">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-3xl animate-pulse-slow" />
      </div>

      {/* Header */}
      <div className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0f]/95 via-[#0f0f1a]/95 to-[#0a0a0f]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
        <div className="px-4 md:px-8 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)} 
                className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center hover:from-purple-500/30 hover:to-blue-500/30 transition-all duration-300"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </motion.button>
              <h1 className="text-white font-rajdhani font-bold text-xl md:text-2xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Player Profile
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="h-7 px-3 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full border border-purple-500/20 hidden md:flex items-center justify-center">
  <span className="text-purple-400 font-rajdhani font-bold text-xs leading-none">
    @{profile?.userName}
  </span>
</div>
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 px-3 py-1 rounded-full border border-green-500/20">
                <span className="text-green-400 font-rajdhani font-bold text-xs flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  ONLINE
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-20 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Profile Header Card */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mb-8"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-2xl blur-xl" />
          <div className="relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-6 border border-purple-500/30 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                  <span className="text-white text-4xl md:text-5xl font-rajdhani font-bold">
                    {profile?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                  </span>
                </div>
                <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-1">
                  <Shield className="w-4 h-4 text-white" />
                </div>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  {profile?.name}
                </h2>
                <p className="text-gray-400 text-sm md:text-base">@{profile?.userName}</p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-3">
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <Calendar className="w-3 h-3" />
                    <span>Joined {formatJoinDate(profile?.createdAt)}</span>
                  </div>
                  {profile?.myTeamId && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 rounded-full">
                      <Users className="w-3 h-3 text-purple-400" />
                      <span className="text-purple-400 text-xs">Team Member</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className={`px-4 py-2 rounded-xl ${currentTier.bgColor} border ${currentTier.borderColor}`}>
                <div className="flex items-center gap-2">
                  {currentTier.icon}
                  <div>
                    <p className={`text-xs font-rajdhani ${currentTier.color}`}>Current Rank</p>
                    <p className={`text-lg font-rajdhani font-bold ${currentTier.color}`}>
                      {currentTier.displayName}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="group relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-white/10 hover:border-purple-500/30 transition-all duration-300 hover:scale-105">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 to-blue-500/0 rounded-2xl group-hover:from-purple-500/10 group-hover:to-blue-500/10 transition-all duration-300" />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <IndianRupee className="w-6 h-6 text-yellow-500" />
                <TrendingUp className="w-4 h-4 text-green-400 opacity-0 group-hover:opacity-100 transition" />
              </div>
              <p className="text-gray-400 text-xs mb-1">Wallet Balance</p>
              <p className="text-white font-rajdhani font-bold text-2xl">
                ₹{profile?.balance?.toLocaleString() || 0}
              </p>
            </div>
          </div>
          
          <div className="group relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-white/10 hover:border-purple-500/30 transition-all duration-300 hover:scale-105">
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <Trophy className="w-6 h-6 text-yellow-500" />
                <Sparkles className="w-4 h-4 text-purple-400 opacity-0 group-hover:opacity-100 transition" />
              </div>
              <p className="text-gray-400 text-xs mb-1">Total Winnings</p>
              <p className="text-white font-rajdhani font-bold text-2xl">
                ₹{profile?.winnings?.toLocaleString() || 0}
              </p>
            </div>
          </div>
          
          <div className="group relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-white/10 hover:border-purple-500/30 transition-all duration-300 hover:scale-105">
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <Sword className="w-6 h-6 text-purple-400" />
                <Target className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition" />
              </div>
              <p className="text-gray-400 text-xs mb-1">Tournaments Played</p>
              <p className="text-white font-rajdhani font-bold text-2xl">
                {profile?.matchPlayed || 0}
              </p>
            </div>
          </div>
          
          <div className="group relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-white/10 hover:border-purple-500/30 transition-all duration-300 hover:scale-105">
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <Medal className="w-6 h-6 text-blue-400" />
                <Star className="w-4 h-4 text-yellow-400 opacity-0 group-hover:opacity-100 transition" />
              </div>
              <p className="text-gray-400 text-xs mb-1">Total Finishes</p>
              <p className="text-white font-rajdhani font-bold text-2xl">
                {profile?.finishes}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Tier & Game Credentials */}
          <div className="lg:col-span-1 space-y-6">
            {/* Tier System */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl border border-purple-500/30 overflow-hidden"
            >
              <div className="p-5 border-b border-purple-500/20">
                <h3 className="text-white font-rajdhani font-bold text-lg flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Tier Progression
                </h3>
                <p className="text-gray-500 text-xs mt-1">Climb the ranks to unlock rewards</p>
              </div>
              
              <div className="p-5 space-y-4">
                {tiersWithStatus.map((tier, index) => (
                  <motion.div
                    key={tier.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative rounded-xl p-4 border transition-all duration-300 ${
                      tier.status === 'current' 
                        ? `${tier.bgColor} ${tier.borderColor} border-2 shadow-lg shadow-purple-500/20` 
                        : tier.status === 'unlocked'
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-white/5 border-white/10 opacity-70'
                    } hover:scale-105 transition-transform`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={tier.color}>
                          {tier.icon}
                        </div>
                        <div>
                          <div className={`font-rajdhani font-bold text-lg ${tier.color}`}>
                            {tier.displayName}
                          </div>
                          <p className="text-gray-500 text-xs mt-1">{tier.requirements}</p>
                        </div>
                      </div>
                      <div>
                        {tier.status === 'current' && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                            Current
                          </span>
                        )}
                        {tier.status === 'unlocked' && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Achieved
                          </span>
                        )}
                        {tier.status === 'locked' && (
                          <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded-full flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Locked
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Game Credentials */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl border border-purple-500/30 overflow-hidden"
            >
              <div className="p-5 border-b border-purple-500/20">
                <h3 className="text-white font-rajdhani font-bold text-lg flex items-center gap-2">
                  <Gamepad2 className="w-5 h-5 text-purple-400" />
                  Game Credentials
                </h3>
                <p className="text-gray-500 text-xs mt-1">Update your IGN and ID for each game</p>
              </div>
              
              {/* BGMI */}
              <div className="p-5 border-b border-purple-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🎮</span>
                    <span className="text-white font-rajdhani font-bold">BGMI</span>
                  </div>
                  {editingGame !== 'BGMI' && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setEditingGame('BGMI');
                        setEditIGN(profile?.BGMIIGN || '');
                        setEditID(profile?.BGMIID || '');
                      }}
                      className="text-purple-400 hover:text-purple-300 flex items-center gap-1 text-sm"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </motion.button>
                  )}
                </div>
                
                <AnimatePresence mode="wait">
                  {editingGame === 'BGMI' ? (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1}}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                    >
                      <input
                        type="text"
                        value={editIGN}
                        onChange={(e) => setEditIGN(e.target.value)}
                        placeholder="In-Game Name (IGN)"
                        className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                      <input
                        type="text"
                        value={editID}
                        onChange={(e) => setEditID(e.target.value)}
                        placeholder="In-Game ID"
                        className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingGame(null)}
                          className="flex-1 px-3 py-2 bg-red-500/20 rounded-lg text-red-400 text-sm font-rajdhani font-bold"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleUpdateGameCredentials('BGMI')}
                          disabled={saving}
                          className="flex-1 px-3 py-2 bg-green-500 rounded-lg text-white text-sm font-rajdhani font-bold disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Save
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="grid grid-cols-2 gap-4"
                    >
                      <div>
                        <p className="text-gray-400 text-xs mb-1">IGN</p>
                        <p className="text-white font-rajdhani">
                          {profile?.BGMIIGN || <span className="text-gray-500">Not set</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">ID</p>
                        <p className="text-white font-rajdhani">
                          {profile?.BGMIID || <span className="text-gray-500">Not set</span>}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* FFMAX */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🔥</span>
                    <span className="text-white font-rajdhani font-bold">Free Fire Max</span>
                  </div>
                  {editingGame !== 'FFMAX' && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setEditingGame('FFMAX');
                        setEditIGN(profile?.FFMAXIGN || '');
                        setEditID(profile?.FFMAXID || '');
                      }}
                      className="text-purple-400 hover:text-purple-300 flex items-center gap-1 text-sm"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </motion.button>
                  )}
                </div>
                
                <AnimatePresence mode="wait">
                  {editingGame === 'FFMAX' ? (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1}}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                    >
                      <input
                        type="text"
                        value={editIGN}
                        onChange={(e) => setEditIGN(e.target.value)}
                        placeholder="In-Game Name (IGN)"
                        className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                      <input
                        type="text"
                        value={editID}
                        onChange={(e) => setEditID(e.target.value)}
                        placeholder="In-Game ID"
                        className="w-full bg-white/5 border border-purple-500/30 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingGame(null)}
                          className="flex-1 px-3 py-2 bg-red-500/20 rounded-lg text-red-400 text-sm font-rajdhani font-bold"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleUpdateGameCredentials('FFMAX')}
                          disabled={saving}
                          className="flex-1 px-3 py-2 bg-green-500 rounded-lg text-white text-sm font-rajdhani font-bold disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Save
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="grid grid-cols-2 gap-4"
                    >
                      <div>
                        <p className="text-gray-400 text-xs mb-1">IGN</p>
                        <p className="text-white font-rajdhani">
                          {profile?.FFMAXIGN || <span className="text-gray-500">Not set</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">ID</p>
                        <p className="text-white font-rajdhani">
                          {profile?.FFMAXID || <span className="text-gray-500">Not set</span>}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Recent Tournaments & Info */}
          <div className="lg:col-span-2 space-y-6">

            {/* Profile Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl border border-purple-500/30 overflow-hidden"
            >
              <div className="p-5 border-b border-purple-500/20">
                <h3 className="text-white font-rajdhani font-bold text-lg flex items-center gap-2">
                  <Info className="w-5 h-5 text-purple-400" />
                  Personal Information
                </h3>
              </div>
              
              <div className="divide-y divide-purple-500/20">
                <div className="p-5 flex items-center gap-4">
                  <User className="w-5 h-5 text-purple-400" />
                  <div className="flex-1">
                    <p className="text-gray-400 text-xs">Full Name</p>
                    <p className="text-white font-rajdhani font-bold">{profile?.name || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="p-5 flex items-center gap-4">
                  <Mail className="w-5 h-5 text-purple-400" />
                  <div className="flex-1">
                    <p className="text-gray-400 text-xs">Email Address</p>
                    <p className="text-white font-rajdhani font-bold">{user?.email}</p>
                  </div>
                </div>
                
                <div className="p-5 flex items-center gap-4">
                  <Phone className="w-5 h-5 text-purple-400" />
                  <div className="flex-1">
                    <p className="text-gray-400 text-xs">Phone Number</p>
                    <p className="text-white font-rajdhani font-bold">{profile?.phone || 'Not added'}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl border border-purple-500/30 overflow-hidden"
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/dashboard/wallet')}
                  className="flex items-center gap-2 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition group"
                >
                  <IndianRupee className="w-4 h-4 text-yellow-500" />
                  <span className="text-white text-sm font-rajdhani">Wallet</span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/dashboard/my-team')}
                  className="flex items-center gap-2 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition group"
                >
                  <Users className="w-4 h-4 text-purple-400" />
                  <span className="text-white text-sm font-rajdhani">My Team</span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/dashboard/leaderboard')}
                  className="flex items-center gap-2 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition group"
                >
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  <span className="text-white text-sm font-rajdhani">Leaderboard</span>
                </motion.button>
              </div>
            </motion.div>

            {/* Logout Button */}
<motion.button
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.5 }}
  whileHover={{ scale: 1.02 }}
  whileTap={{ scale: 0.98 }}
  onClick={() => setShowLogoutModal(true)} 
  className="w-full bg-red-500/10 py-4 rounded-xl text-red-400 font-rajdhani font-bold flex items-center justify-center gap-2 border border-red-500/30 hover:bg-red-500/20 transition-all duration-300"
>
  <LogOut className="w-5 h-5" /> Logout
</motion.button>
          </div>
        </div>
        {/* Logout Confirmation Modal */}
<AnimatePresence>
  {showLogoutModal && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
      onClick={() => setShowLogoutModal(false)}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-md w-full p-6 border border-red-500/30 shadow-2xl"
      >
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <LogOut className="w-10 h-10 text-red-400" />
          </div>
          
          <h2 className="text-white font-rajdhani font-bold text-2xl mb-2">
            Logout Confirmation
          </h2>
          
          <p className="text-gray-400 text-sm mb-6">
            Are you sure you want to logout from your account? You'll need to login again to access your profile.
          </p>
          
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowLogoutModal(false)}
              className="flex-1 py-3 bg-white/10 rounded-xl text-white font-rajdhani font-bold hover:bg-white/20 transition-all"
            >
              Cancel
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleLogout}
              disabled={loggingOut}
              className="flex-1 py-3 bg-gradient-to-r from-red-500 to-red-600 rounded-xl text-white font-rajdhani font-bold hover:shadow-lg hover:shadow-red-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loggingOut ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Logging out...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4" />
                  Yes, Logout
                </>
              )}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        .font-rajdhani {
          font-family: 'Rajdhani', sans-serif;
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Profile;